import { Assert, jsonPointerGet, splitTemplate } from '@cotera/utilities';

/**
 * A declarative spec for deriving a dedupe key from a consumer's inputs.
 *
 * Resource-agnostic on purpose: workflows resolve it against run inputs, agents
 * against invoke inputs. The consumer decides what "inputs" means and where the
 * resolved key is stored; this module only turns a spec plus a value into a
 * stable string.
 */
export const IDEMPOTENCY_KEY_STRATEGIES = ['value'] as const;

export type IdempotencyKeyStrategy =
  (typeof IDEMPOTENCY_KEY_STRATEGIES)[number];

/** Resolves a dedupe key from inputs via a single JSON Pointer. */
export type IdempotencyKeyValueStrategy = {
  readonly strategy: 'value';
  /** RFC 6901 JSON Pointer into the consumer's inputs. */
  readonly pointer: string;
};

/** Resolves a dedupe key by interpolating several pointers into a template. */
export type IdempotencyKeyCompositeStrategy = {
  readonly strategy: 'value';
  /** Template of JSON Pointer tokens resolved against the consumer's inputs. */
  readonly template: string;
  readonly pointer?: never;
};

export type IdempotencyKeySpec =
  | IdempotencyKeyValueStrategy
  | IdempotencyKeyCompositeStrategy;

export type ParseIdempotencyKeyResult =
  | { readonly ok: true; readonly config: IdempotencyKeySpec }
  | { readonly ok: false; readonly error: string };

export type ResolveIdempotencyKeyResult =
  | { readonly ok: true; readonly key: string }
  | { readonly ok: false; readonly error: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function isIdempotencyKeyStrategy(
  value: unknown
): value is IdempotencyKeyStrategy {
  return (
    typeof value === 'string' &&
    (IDEMPOTENCY_KEY_STRATEGIES as readonly string[]).includes(value)
  );
}

export function parseIdempotencyKeySpec(
  value: unknown
): ParseIdempotencyKeyResult {
  const record = asRecord(value);
  if (record === null) {
    return { ok: false, error: 'Idempotency key must be an object' };
  }

  const strategy = record['strategy'];
  if (!isIdempotencyKeyStrategy(strategy)) {
    return {
      ok: false,
      error: `Unknown idempotency key strategy: ${String(strategy)}`,
    };
  }

  switch (strategy) {
    case 'value': {
      const pointer = record['pointer'];
      const template = record['template'];
      if (typeof pointer === 'string' && pointer.trim() !== '') {
        return { ok: true, config: { strategy: 'value', pointer } };
      }
      if (typeof template !== 'string' || template.trim() === '') {
        return {
          ok: false,
          error: 'Value strategy requires a non-empty JSON Pointer or template',
        };
      }
      return { ok: true, config: { strategy: 'value', template } };
    }
    default:
      return Assert.unreachable(strategy);
  }
}

/**
 * Serializes a resolved input value into a stable string for deduplication.
 */
export function idempotencyValueToKey(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function resolveTemplateRef(params: {
  readonly inputs: unknown;
  readonly ref: string;
}): ResolveIdempotencyKeyResult {
  let resolved: unknown;
  try {
    resolved = jsonPointerGet(params.inputs, params.ref);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Invalid JSON Pointer';
    return {
      ok: false,
      error: `Failed to resolve idempotency pointer "${params.ref}": ${message}`,
    };
  }
  const key = idempotencyValueToKey(resolved);
  if (key === null) {
    return {
      ok: false,
      error: `Idempotency pointer "${params.ref}" resolved to null or undefined`,
    };
  }
  return { ok: true, key };
}

/**
 * Resolves the dedupe key from a spec and the consumer's inputs. Returns
 * `{ ok: false }` when the pointer is missing or resolves to null/undefined.
 */
export function resolveIdempotencyKey(params: {
  readonly config: IdempotencyKeySpec | null;
  readonly inputs: unknown;
}): ResolveIdempotencyKeyResult {
  const { config, inputs } = params;
  if (config === null || config === undefined) {
    return { ok: false, error: 'No idempotency key configured' };
  }

  switch (config.strategy) {
    case 'value': {
      // A pointer is a template with a single ref and no literal text, so both
      // forms fold through one loop.
      const segments =
        'pointer' in config && typeof config.pointer === 'string'
          ? [{ t: 'ref' as const, ref: config.pointer }]
          : splitTemplate('template' in config ? config.template : '');

      const parts: string[] = [];
      for (const segment of segments) {
        if (segment.t === 'text') {
          parts.push(segment.value);
          continue;
        }
        const resolved = resolveTemplateRef({ inputs, ref: segment.ref });
        if (!resolved.ok) {
          return resolved;
        }
        parts.push(resolved.key);
      }

      const key = parts.join('');
      if (key.trim() === '') {
        return {
          ok: false,
          error: 'Idempotency key resolved to an empty value',
        };
      }
      return { ok: true, key };
    }
    default:
      return Assert.unreachable(config.strategy);
  }
}
